import { useCallback, useEffect } from 'react';
import { useChangePasswordMutation } from '../redux/features/api';
import { useValidationForm } from './useValidationForm';
import { useRecaptcha } from './useRecaptcha';
import {
  trackPasswordChangeSubmit,
  trackPasswordChangeSuccess,
  trackPasswordChangeError
} from '../helpers';

const schema = {
  currentPassword: {
    presence: { allowEmpty: false, message: 'is required' },
    length: {
      minimum: 1
    }
  },
  newPassword: {
    presence: { allowEmpty: false, message: 'is required' },
    length: {
      minimum: 8,
      message: 'must be at least 8 characters'
    }
  },
  confirmPassword: {
    presence: { allowEmpty: false, message: 'is required' },
    equality: {
      attribute: 'newPassword',
      message: 'passwords do not match'
    }
  }
};

export const useChangePassword = () => {
  const [changePassword, { isLoading, isSuccess, isError, error }] =
    useChangePasswordMutation();
  const { executeRecaptcha } = useRecaptcha();
  const { formState, handleChange, hasError, resetForm } = useValidationForm({
    schemaToValidate: schema
  });

  useEffect(() => {
    // Track errors
    if (isError && error) {
      const errorMessage = error?.data?.message || error?.message || '';
      let errorType = 'unknown_error';

      if (errorMessage.toLowerCase().includes('incorrect')) {
        errorType = 'incorrect_current_password';
      } else if (errorMessage.toLowerCase().includes('same')) {
        errorType = 'same_password';
      } else if (errorMessage.toLowerCase().includes('password')) {
        errorType = 'validation_error';
      }

      trackPasswordChangeError(errorType);
    }
  }, [isError, error]);

  useEffect(() => {
    // Track success and reset form
    if (isSuccess) {
      trackPasswordChangeSuccess();
      // Reset form after successful change
      setTimeout(() => {
        resetForm();
      }, 2000);
    }
  }, [isSuccess, resetForm]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!formState.isValid) {
        return;
      }

      try {
        // Track submission
        trackPasswordChangeSubmit();

        // Get reCAPTCHA token
        const recaptchaToken = await executeRecaptcha('change_password');

        // Call the change password API
        await changePassword({
          currentPassword: formState.values.currentPassword,
          newPassword: formState.values.newPassword,
          recaptchaToken
        }).unwrap();
      } catch (error) {
        console.error('Change password error:', error);
      }
    },
    [
      formState.isValid,
      formState.values.currentPassword,
      formState.values.newPassword,
      changePassword,
      executeRecaptcha
    ]
  );

  return {
    formState,
    handleChange,
    handleSubmit,
    hasError,
    isLoading,
    isSuccess,
    isError
  };
};

